<template>
  <div>
    <RevButtonIcon
      v-if="isRounded"
      :alternative-text="buttonLabel"
      data-qa="my-favorites-toggle"
      :disabled="isDisabled"
      :icon="heartComponentVariant"
      variant="ghost"
      v-bind="$attrs"
      @click="handleClick"
    />

    <RevButton
      v-if="!isRounded"
      :aria-label="buttonLabel"
      class="h-48 w-48"
      data-qa="my-favorites-toggle"
      :disabled="isDisabled"
      size="small"
      variant="secondary"
      v-bind="$attrs"
      @click="handleClick"
    >
      <component :is="heartComponentVariant" />
    </RevButton>
  </div>
</template>

<script setup lang="ts">
import { navigateTo, useRoute } from '#imports'
import { computed, ref, watch } from 'vue'

import type { MonetaryAmount } from '@backmarket/http-api/src/api-models/MonetaryAmount'
import { HttpApiError } from '@backmarket/http-api/src/utils/HttpApiError'
import { useUserStore } from '@backmarket/nuxt-layer-oauth/useUserStore'
import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useLogger } from '@backmarket/nuxt-module-logger/useLogger'
import type { EventData } from '@backmarket/nuxt-module-tracking'
import type { TrackingFunctions } from '@backmarket/nuxt-module-tracking/types'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { RevButton } from '@ds/components/Button'
import { RevButtonIcon } from '@ds/components/ButtonIcon'
import { IconHeart } from '@ds/icons/IconHeart'
import { IconHeartFilled } from '@ds/icons/IconHeartFilled'

import type { FavoriteMinimal } from '~/api/favorites/types'
import { TRACKING_PAGE_TYPE_BY_ROUTE_NAME } from '~/constants/trackingPageType'
import { ROUTES } from '~/scopes/auth/route-names'

import {
  FAVORITES_STORAGE_PREFIX,
  FavoritesAction,
  LOG_ERROR_MESSAGE,
} from './FavoritesToggle'
import translations from './FavoritesToggle.translations'
import { useFavoritesToast } from './toast/useFavoritesToast'

const i18n = useI18n()
const { isUserAuthenticated } = useUserStore()
const route = useRoute()
const logger = useLogger()
const { favoritesToast, isRemoveFavoriteToastOpen } = useFavoritesToast()
const { trackAddToMyFavorites, trackAddToFavorite, trackClick } = useTracking()

type FavoriteTrackingInfo = Parameters<
  TrackingFunctions['trackAddToFavorite']
>[0]
const $httpFetch = useHttpFetch()

defineOptions({
  inheritAttrs: false,
})

const props = withDefaults(
  defineProps<{
    favoriteId?: string
    gradeId?: number
    isRounded?: boolean
    offerType: number | null
    price?: MonetaryAmount | null
    productUuid: string
    zone?: string
    trackingInfo?: Record<string, unknown>
    favoriteTrackingInfo?: FavoriteTrackingInfo
  }>(),
  {
    favoriteId: undefined,
    gradeId: undefined,
    isRounded: false,
    price: undefined,
    offerType: 0,
    zone: 'details',
    trackingInfo: () => ({}),
    favoriteTrackingInfo: undefined,
  },
)

const emit = defineEmits(['removed'])

const isFavorite = ref(Boolean(props.favoriteId))
const localFavoriteId = ref(props.favoriteId)
const isAddingToFavorites = ref(false)

function resetData() {
  isFavorite.value = Boolean(props.favoriteId)
  localFavoriteId.value = props.favoriteId
}

async function getFavoriteFromProduct() {
  if (isFavorite.value || !isUserAuthenticated.value) {
    return
  }

  const offerType = props.offerType ?? 0
  const query = {
    ...(props.gradeId !== undefined && {
      gradeId: props.gradeId,
    }),
    offerType,
  }

  try {
    const data = await $httpFetch(
      '/my-favorites/favorites/products/:productUuid',
      {
        method: 'GET',
        pathParams: { productUuid: props.productUuid },
        query,
      },
    )

    const [favorite] = data || ([] as FavoriteMinimal[])

    if (!favorite) {
      return
    }

    localFavoriteId.value = favorite.favoriteId
    isFavorite.value = true
  } catch (err) {
    const error = HttpApiError.fromAnyError(err)

    logger.error(LOG_ERROR_MESSAGE.CHECK, {
      error,
      feature: 'favorites',
      favorite_action: 'check',
      user_authenticated: 'true',
      api_endpoint: '/my-favorites/favorites/products/:productUuid',
      error_type: error.status >= 500 ? 'api_5xx' : 'api_4xx',
      error_status: error.status,
      product_uuid: props.productUuid,
      grade_id: props.gradeId,
      offer_type: props.offerType ?? 0,
      owners: ['bot-squad-lifecycle-front'],
    })
  }
}

const isDisabled = computed(() => {
  return isRemoveFavoriteToastOpen.value || !props.gradeId || !props.price
})

const buttonLabel = computed(() => {
  return isFavorite.value
    ? i18n(translations.favoritesButtonRemoveLabel)
    : i18n(translations.favoritesButtonAddLabel)
})

const uniqueStorageKey = computed(() => {
  const key = `${FAVORITES_STORAGE_PREFIX}_${props.productUuid}_${props.gradeId}_${props.offerType}`
  const keyForRounded = `${key}_rounded`

  return props.isRounded ? keyForRounded : key
})

const heartComponentVariant = computed(() => {
  return isFavorite.value ? IconHeartFilled : IconHeart
})

function storePendingAction(pendingAction: FavoritesAction) {
  sessionStorage?.setItem(uniqueStorageKey.value, pendingAction)
}

async function requestAuthentication() {
  const next = route.fullPath

  await navigateTo({
    name: ROUTES.AUTH.LOGIN,

    query: {
      next,

      // needed for the info toast on Auth page
      info: 'my-favorites',
      bm_journey: 'favorites',
    },
  })
}

async function addToFavorites() {
  if (isFavorite.value) {
    favoritesToast.openAlreadySavedFavToast()

    return
  }

  if (isAddingToFavorites.value) {
    return
  }

  const { productUuid, gradeId, price } = props

  if (!gradeId || !price) {
    logger.error(LOG_ERROR_MESSAGE.ADD, {
      feature: 'favorites',
      favorite_action: 'add',
      error_type: 'validation',
      product_uuid: productUuid,
      grade_id: gradeId,
      owners: ['bot-squad-lifecycle-front'],
    })

    return
  }

  const offerType = props.offerType ?? 0

  isAddingToFavorites.value = true
  isFavorite.value = true

  try {
    const data = await $httpFetch('/my-favorites/favorites', {
      method: 'POST',
      body: { productUuid, gradeId, price, offerType },
    })

    const { favoriteId } = data ?? { favoriteId: '' }

    favoritesToast.openSavedFavToast()

    localFavoriteId.value = favoriteId
  } catch (err) {
    const error = HttpApiError.fromAnyError(err)

    const errorType = error.status >= 500 ? 'api_5xx' : 'api_4xx'

    const errorRecoverable = [429, 503, 408].includes(error.status)
      ? 'true'
      : 'false'

    isFavorite.value = false

    logger.error(LOG_ERROR_MESSAGE.ADD, {
      error,
      feature: 'favorites',
      favorite_action: 'add',
      user_authenticated: isUserAuthenticated.value ? 'true' : 'false',
      api_endpoint: '/my-favorites/favorites',
      optimistic_ui: 'enabled',
      error_type: errorType,
      error_status: error.status,
      error_recoverable: errorRecoverable,
      product_uuid: productUuid,
      grade_id: gradeId,
      offer_type: offerType,
      owners: ['bot-squad-lifecycle-front'],
    })
  } finally {
    isAddingToFavorites.value = false
  }
}

async function removeFromFavorites() {
  const favoriteId = localFavoriteId.value ?? ''

  try {
    await $httpFetch('/my-favorites/favorites/:favoriteId', {
      method: 'DELETE',
      pathParams: { favoriteId },
    })

    localFavoriteId.value = ''
    isFavorite.value = false

    emit('removed')
  } catch (err) {
    const error = HttpApiError.fromAnyError(err)

    const errorType = error.status >= 500 ? 'api_5xx' : 'api_4xx'

    const errorRecoverable = [429, 503, 408].includes(error.status)
      ? 'true'
      : 'false'

    // Rollback optimistic state when API call fails
    isFavorite.value = true

    logger.error(LOG_ERROR_MESSAGE.REMOVE, {
      error,
      feature: 'favorites',
      favorite_action: 'remove',
      user_authenticated: isUserAuthenticated.value ? 'true' : 'false',
      api_endpoint: '/my-favorites/favorites/:favoriteId',
      optimistic_ui: 'enabled',
      error_type: errorType,
      error_status: error.status,
      error_recoverable: errorRecoverable,
      favorite_id: favoriteId,
      product_uuid: props.productUuid,
      owners: ['bot-squad-lifecycle-front'],
    })
  }
}

function clearPendingAction() {
  sessionStorage?.removeItem(uniqueStorageKey.value)
}

function executePendingAction() {
  if (!import.meta.client || !isUserAuthenticated.value) {
    return
  }

  const action = sessionStorage?.getItem(uniqueStorageKey.value)
  clearPendingAction()

  if (!action) {
    return
  }

  try {
    if (action === FavoritesAction.Add) {
      addToFavorites()
    } else if (action === FavoritesAction.Remove) {
      removeFromFavorites()
    }
  } catch (err) {
    const error = HttpApiError.fromAnyError(err)

    const errorType = error.status >= 500 ? 'api_5xx' : 'api_4xx'

    const pendingAction = action === FavoritesAction.Add ? 'add' : 'remove'

    logger.error(LOG_ERROR_MESSAGE.PENDING, {
      error,
      feature: 'favorites',
      favorite_action: pendingAction,
      user_authenticated: 'true',
      has_pending_actions: 'true',
      error_type: errorType,
      error_status: error.status,
      product_uuid: props.productUuid,
      grade_id: props.gradeId,
      owners: ['bot-squad-lifecycle-front'],
    })
  }
}

async function initialAction() {
  if (!isUserAuthenticated.value) {
    return
  }

  await getFavoriteFromProduct()
  executePendingAction()
}

function trackAddClick() {
  trackAddToMyFavorites(props.trackingInfo as EventData)

  // 🚧 GA4 migration (BB-26): fire add_to_wishlist alongside the legacy event.
  // Only PDP passes favoriteTrackingInfo, so other entry points stay on legacy.
  if (props.favoriteTrackingInfo) {
    trackAddToFavorite(props.favoriteTrackingInfo)
  }
}

const pageType = computed(() => {
  const routeName = String(route.name)

  return TRACKING_PAGE_TYPE_BY_ROUTE_NAME[routeName]
})

function trackRemoveClick() {
  const name = 'remove_from_my_favorites'

  trackClick({ name, pageType: pageType.value, zone: props.zone })
}

async function handleClick() {
  const doTrackClick = isFavorite.value ? trackRemoveClick : trackAddClick
  doTrackClick()

  if (!isUserAuthenticated.value) {
    storePendingAction(FavoritesAction.Add)

    await requestAuthentication()

    return
  }

  if (isFavorite.value) {
    storePendingAction(FavoritesAction.Remove)

    favoritesToast.openRemoveFavToast(
      () => {
        // Undo callback
        isFavorite.value = true
        clearPendingAction()
      },
      () => {
        // Close callback
        executePendingAction()
      },
    )

    // optimistic UI: displays an empty heart right away,
    // even though the favorite has not been removed from the database yet
    isFavorite.value = false

    return
  }

  addToFavorites()
}

function optionUpdated() {
  resetData()
  initialAction()
}

watch(isUserAuthenticated, () => {
  optionUpdated()
})

watch(
  () => props.productUuid,
  () => optionUpdated(),
)

watch(
  // reacts to any item option change that is reflected in the URL
  () => route.fullPath,
  () => {
    optionUpdated()
  },
)

initialAction()
</script>
